import {
  DownOutlined,
  InfoCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import {
  defaultPaymentFrequency,
  defaultPaymentMethod,
  getPaymentMethodOptions,
  defaultRightToWorkStatus,
  defaultStatusOptions,
  genderOptions,
  maritalStatusOption,
  PensionProvidersName,
  reasonOptions,
  regStatusEditOptions,
  regulationOptions,
  titleOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { NationalList } from '@nxsys/constants/countries';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useUpdateEmployeeById } from '@nxsys/hooks/api';
import {
  useDownloadP60Employee,
  useSendEmailP60Employee,
  useUpdateSettingEmployeeById,
} from '@nxsys/hooks/api/employee/useEmployeeApi';
import {
  useSubmitIssueP45BySubcontractorId,
  useValidateIssueP45BySubcontractorId,
} from '@nxsys/hooks/api/subcontractor/useSubcontractorApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { downloadLatestHistoryLeaverApi } from '@nxsys/services/api/subcontractor';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { UpdateEmployeeByIdParams } from '@nxsys/types/api';
import { RetainedP45PayoutDecision } from '@nxsys/types/api/subcontractor';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import {
  countryList,
  isValidNINumber,
  isValidPPSNumber,
  showCurrencyByCountry,
} from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { validatePhone } from '@nxsys/utils/validator';
import {
  Col,
  Divider,
  Dropdown,
  Form,
  Modal,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import { includes } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SubmitIssueP45Modal, {
  SubmitIssueP45Values,
} from '../../reports/management/components/SubmitIssueP45Modal';
import ModalReP45 from './components/ModalReP45';
import P60Modal, { P60Values } from './components/P60Modal';
import ReinstatementModal from './components/ReinstatementModal';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import { useDrawerSave } from './useDrawerSave';

type Props = {
  data: EmployeeRecordType | undefined;
  onRefetch?: () => void;
  readOnly?: boolean;
  readOnlyContact?: boolean;
  onResendLogin?: () => void;
  isResendLoginLoading?: boolean;
  /** Field name to scroll to + focus once data loads (NX-4682 quick-edit). */
  focusField?: string;
};

const GeneralInformation = ({
  data,
  onRefetch,
  readOnly,
  readOnlyContact,
  onResendLogin,
  isResendLoginLoading,
  focusField,
}: Props) => {
  const { messages } = useTypedIntl();
  const routerNavigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isEmployee, payrollCountry, isBureau, isBureauEmployer, appConfigs } =
    useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);

  // Scroll to + focus a field when opened from the dashboard "Action Required"
  // quick-edit drawer (NX-4682). No-op on the standalone detail page.
  useEffect(() => {
    if (!focusField || !data) return;
    const timer = setTimeout(() => {
      // AppInput doesn't forward focus() to its native input, and antd prefixes
      // field ids with the form name — target the control via its id suffix.
      const el = document.querySelector<HTMLElement>(
        `[id="${focusField}"], [id$="_${focusField}"]`,
      );
      if (el) {
        // Smoothly center the whole field, then focus WITHOUT re-scrolling so
        // the browser's focus auto-scroll doesn't clobber the animation.
        const target = el.closest('.ant-form-item') ?? el;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      } else {
        form.scrollToField(focusField, { behavior: 'smooth', block: 'center' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [focusField, data, form]);

  const computedAge =
    watchValues?.DOB && moment(watchValues.DOB).isValid()
      ? Math.floor(moment().diff(moment(watchValues.DOB), 'years', true))
      : null;

  // P45 state
  const [isOpenSubmitP45Modal, setIsOpenSubmitP45Modal] = useState(false);
  // NX-3476 (QA E3/E4): backend explanation for why no pension refund was
  // raised — surfaced in the submit modal on the no-refund path.
  const [p45NoRefundReason, setP45NoRefundReason] = useState<string>();
  const [isOpenReP45Modal, setIsOpenReP45Modal] = useState(false);
  // Whether the ReP45 picker (multiple issued P45s) is downloading or emailing.
  const [reP45Action, setReP45Action] = useState<'DOWNLOAD' | 'SENDMAIL'>(
    'DOWNLOAD',
  );
  const [p45DefaultDate, setP45DefaultDate] = useState<Moment | null>(null);
  const [p45RetainDecision, setP45RetainDecision] = useState<
    RetainedP45PayoutDecision | undefined
  >();
  // P60 state
  const [isOpenP60DownloadModal, setIsOpenP60DownloadModal] = useState(false);
  const [isOpenP60EmailModal, setIsOpenP60EmailModal] = useState(false);
  // Reinstatement state
  const [isOpenReinstatementModal, setIsOpenReinstatementModal] =
    useState(false);

  // P45 hooks
  const { run: validateIssueP45, loading: isValidateP45Loading } =
    useValidateIssueP45BySubcontractorId();
  const { run: submitIssueP45, loading: isSubmitP45Loading } =
    useSubmitIssueP45BySubcontractorId();
  const { run: updateEmployee, loading: isUpdatingEmployee } =
    useUpdateEmployeeById();
  const { run: updateSettingEmployee, loading: isUpdatingSettingEmployee } =
    useUpdateSettingEmployeeById();
  // P60 hooks
  const { run: downloadP60 } = useDownloadP60Employee();
  const { run: sendEmailP60 } = useSendEmailP60Employee();

  // P45 computed flags
  const isPayeeUmbrellaHybrid = data
    ? includes(UMBRELLA_PAYE_HYBRID, data.setting?.paymentModel)
    : false;
  const isShowIssueP45 = !data?.isIssuedP45;

  const paymentModel = data?.setting?.paymentModel;
  const isShowDownloadP60 = useMemo(() => {
    if (isIreland) return false;
    const payeUmbrellaPaymentModels =
      appConfigs?.payeUmbrellaPaymentModels ?? [];
    return payeUmbrellaPaymentModels.includes(paymentModel ?? '');
  }, [isIreland, paymentModel, appConfigs]);

  // Initialize form when data changes
  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        // Personal Detail
        title: data.title
          ? (titleOptions.find((item) => item.value === data.title) ?? null)
          : null,
        forename: data.forename ?? '',
        surname: data.surname ?? '',
        DOB: data.DOB ? moment(data.DOB) : null,
        gender: data.gender
          ? (genderOptions.find((item) => item.value === data.gender) ?? null)
          : null,
        maritalStatus: data.maritalStatus
          ? (maritalStatusOption.find(
              (item) => item.value === data.maritalStatus,
            ) ?? null)
          : null,
        // Ireland stores the PPS number on setting.ppsNumber; NINumber is UK-only (NX-5443)
        NINumber: isIreland
          ? (data.setting?.ppsNumber ?? '')
          : (data.NINumber ?? ''),
        nationality: data.nationality
          ? (NationalList.find((item) => item.value === data.nationality) ??
            null)
          : null,
        email: data.email ?? '',
        mobile: data.mobile ?? '',
        // Right to Work
        rightToWorkStatus: data.rightToWorkStatus
          ? (defaultRightToWorkStatus.find(
              (item) => item.value === data.rightToWorkStatus,
            ) ?? null)
          : null,
        documentExpiryDate: data.documentExpiryDate
          ? moment(data.documentExpiryDate)
          : null,
        document: data.document ?? '',
        shareCodeVerified: data.shareCodeVerified ?? '',
        isRestrictedHours: data.isRestrictedHours ?? false,
        // Registration Detail (non-employee)
        paymentCompany: data.companyPayment
          ? {
              label: data.companyPayment?.companyName,
              value: data.companyPayment?.id,
              fullData: data.companyPayment,
            }
          : null,
        status: data.status
          ? defaultStatusOptions.find((item) => item.value === data.status)
          : null,
        dateOfJoining: data.dateOfJoining ? moment(data.dateOfJoining) : null,
        dateLeft: data.dateLeft ? moment(data.dateLeft) : null,
        payMethod: data.payMethod
          ? defaultPaymentMethod.find((item) => item.value === data.payMethod)
          : null,
        rateHour: data.rateHour ?? '',
        annualSalary: data.annualSalary ?? '',
        workingTimeRegulation: data.workingTimeRegulation
          ? regulationOptions.find(
              (item) => item.value === data.workingTimeRegulation,
            )
          : null,
        agencyRef: data.agencyRef ?? '',
        registrationStatus: data.registrationStatus
          ? regStatusEditOptions.find(
              (item) => item.value === data.registrationStatus,
            )
          : null,
        payFrequency: data.payFrequency
          ? defaultPaymentFrequency.find(
              (item) => item.value === data.payFrequency,
            )
          : null,
        reason: data.reason
          ? reasonOptions.find((item) => item.value === data.reason)
          : null,
        jobDescription: data.jobDescription ?? '',
        hours: data.hours ?? '',
        agencyConductRegulation: data.agencyConductRegulation
          ? regulationOptions.find(
              (item) => item.value === data.agencyConductRegulation,
            )
          : null,
        systemId: data.id ?? '',
        department: data.department
          ? { label: data.department, value: data.department }
          : null,
        // Emergency Contact
        emergencyName: data.emergencyName ?? '',
        emergencyAddress1: data.emergencyAddress?.[0] ?? '',
        emergencyAddress2: data.emergencyAddress?.[1] ?? '',
        emergencyAddress3: data.emergencyAddress?.[2] ?? '',
        emergencyCity: data.emergencyCity ?? '',
        emergencyCounty: data.emergencyCounty ?? '',
        emergencyPostCode: data.emergencyPostCode ?? '',
        emergencyCountry: data.emergencyCountry
          ? countryList.find((item) => item.value === data.emergencyCountry)
          : null,
        emergencyMobile: data.emergencyMobile ?? '',
        emergencyEmail: data.emergencyEmail ?? '',
        // System Access
        allowPortalLogin: data.allowPortalLogin ?? false,
        loginId: data.loginId ?? '',
      });
    }
  }, [data]);

  // --- Save Handler ---
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!data?.id) return;

      const params: Partial<UpdateEmployeeByIdParams> = {
        title: values.title?.value || null,
        forename: values.forename ?? '',
        surname: values.surname ?? '',
        DOB: values.DOB ? moment(values.DOB).toISOString() : '',
        gender: values.gender?.value ?? '',
        maritalStatus: values.maritalStatus?.value || null,
        nationality: values.nationality?.value || null,
        mobile: values.mobile || null,
        email: values.email || null,
        rightToWorkStatus: values.rightToWorkStatus?.value || null,
        documentExpiryDate: values.documentExpiryDate
          ? moment(values.documentExpiryDate).toISOString()
          : null,
        document: data.document || null,
        shareCodeVerified: values.shareCodeVerified ?? '',
        isRestrictedHours: values.isRestrictedHours ?? false,
      };

      // For Ireland the field holds the PPS, which lives on setting.ppsNumber
      // and is saved via the payroll-setting endpoint below (NX-5443); sending
      // it as NINumber would fail the backend's UK NI validation.
      if (!isIreland) {
        params.NINumber = values.NINumber ?? '';
      }

      if (!isEmployee) {
        params.companyPayment = values.paymentCompany?.value || null;
        params.status = values.status?.value || null;
        params.agencyRef = values.agencyRef ?? '';
        params.registrationStatus = values.registrationStatus?.value || null;
        params.dateOfJoining = values.dateOfJoining
          ? moment(values.dateOfJoining).toISOString()
          : '';
        params.payFrequency = values.payFrequency?.value || null;
        params.dateLeft = values.dateLeft
          ? moment(values.dateLeft).toISOString()
          : '';
        params.reason = values.reason?.value || null;
        params.payMethod = values.payMethod?.value || null;
        params.jobDescription = values.jobDescription ?? '';
        params.rateHour = values.rateHour ?? '';
        params.hours = values.hours ?? '';
        params.workingTimeRegulation =
          values.workingTimeRegulation?.value || null;
        params.agencyConductRegulation =
          values.agencyConductRegulation?.value || null;
        params.annualSalary = values.annualSalary ?? '';
        params.department = values.department?.value || null;

        // Emergency Contact
        params.emergencyName = values.emergencyName ?? '';
        params.emergencyAddress = [
          values.emergencyAddress1 ?? '',
          values.emergencyAddress2 ?? '',
          values.emergencyAddress3 ?? '',
        ];
        params.emergencyCity = values.emergencyCity ?? '';
        params.emergencyCounty = values.emergencyCounty ?? '';
        params.emergencyPostCode = values.emergencyPostCode ?? '';
        params.emergencyCountry = values.emergencyCountry?.value || null;
        params.emergencyMobile = values.emergencyMobile || null;
        params.emergencyEmail = values.emergencyEmail || null;

        // System Access
        params.allowPortalLogin = values.allowPortalLogin === true;
        params.loginId = values.loginId || null;
      }

      const isUploadFile = !!(values.document as File)?.name;
      if (isUploadFile) {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.document as File }),
        ).unwrap();
        params.document = res.url;
      }

      await updateEmployee(data.id, params as UpdateEmployeeByIdParams);

      if (isIreland) {
        const newPpsNumber = (values.NINumber ?? '').trim().toUpperCase();
        if (newPpsNumber !== (data.setting?.ppsNumber ?? '')) {
          await updateSettingEmployee(data.id, {
            ppsNumber: newPpsNumber || null,
          } as any);
        }
      }

      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      onRefetch?.();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  // --- P45 Handlers ---
  const handleDownloadP45 = async () => {
    if (!data?.id) return;
    // If multiple P45s issued, show the picker in download mode
    const issuedP45s = (data as any)?.issuedP45s;
    if (issuedP45s?.length > 1) {
      setReP45Action('DOWNLOAD');
      setIsOpenReP45Modal(true);
      return;
    }
    try {
      const res = await downloadLatestHistoryLeaverApi(data.id);
      if (res?.data) {
        window.open(res.data);
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  // Email an already-issued P45 to the employee. isManual => the backend
  // re-sends the existing P45 (sendMailIssueP45) rather than issuing a new one.
  const handleEmailP45 = async () => {
    if (!data?.id) return;
    const issuedP45s = (data as any)?.issuedP45s;
    if (issuedP45s?.length > 1) {
      setReP45Action('SENDMAIL');
      setIsOpenReP45Modal(true);
      return;
    }
    try {
      await submitIssueP45(data.id, {
        dateLeft: data?.dateLeft
          ? moment(data.dateLeft).toISOString()
          : (issuedP45s?.[0]?.data?.dateLeft ?? ''),
        reason: data?.reason ?? '',
        isManual: true,
      });
      showMessage({
        type: 'success',
        content:
          messages['common.emailP45Success'] ?? 'P45 emailed successfully',
      });
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleReP45Submit = async (params: { dateLeft: string }) => {
    const issuedP45s = (data as any)?.issuedP45s ?? [];
    const selected = issuedP45s.find(
      (p: any) => p.data?.dateLeft === params.dateLeft,
    );
    if (reP45Action === 'SENDMAIL') {
      if (data?.id) {
        try {
          await submitIssueP45(data.id, {
            dateLeft: params.dateLeft,
            reason: selected?.data?.reason ?? data?.reason ?? '',
            isManual: true,
          });
          showMessage({
            type: 'success',
            content:
              messages['common.emailP45Success'] ?? 'P45 emailed successfully',
          });
        } catch (error: any) {
          console.error(error);
        }
      }
    } else if (selected?.url) {
      window.open(selected.url);
    }
    setReP45Action('DOWNLOAD');
    setIsOpenReP45Modal(false);
  };

  const handleSubmitIssueP45 = (values: SubmitIssueP45Values) => {
    if (!data?.id) return;
    submitIssueP45(data.id, {
      dateLeft: moment(values.dateLeft).toISOString(),
      reason: values.reason?.value,
      retainedPayoutDecision: p45RetainDecision,
    }).then((res) => {
      if (!isIreland) {
        const resData = res?.data?.[0];
        if (resData?.success) {
          showMessage({
            type: 'success',
            content:
              messages['common.issueP45SendSuccess'] ??
              'Issue P45 sent successfully',
          });
        } else {
          showMessage({
            type: 'warning',
            content: resData?.reason ?? 'Issue P45 sent failed',
          });
        }
        if (resData?.url) {
          window.open(resData.url);
        }
      } else {
        if (res?.success) {
          showMessage({
            type: 'success',
            content:
              messages['common.markLeaverSuccess'] ??
              'Mark leaver successfully',
          });
        }
      }
      onRefetch?.();
    });
  };

  const showConfirmP45 = (
    hpBalance: number,
    erCostsBalance: number,
    eePensionRefund: number,
    erPensionRefund: number,
    lastestTaxPeriod?: number,
  ) => {
    const isHp = hpBalance + erCostsBalance > 0;
    // Umbrella/Hybrid: refund both EE + ER (both deducted from employee's gross)
    // PAYE/PAYE_FTH/PAYE_HYBRID: refund only EE (ER is employer's cost)
    const isUmbrella = ['UMBRELLA', 'HYBRID'].includes(data?.setting?.paymentModel ?? '');
    const pensionRefundAmount = isUmbrella
      ? eePensionRefund + erPensionRefund
      : eePensionRefund;
    const isPension = pensionRefundAmount > 0;
    const isBoth = isHp && isPension;
    const type = isBoth ? 'both' : isHp ? 'hp' : 'pension';

    const formattedPensionRefund = formatMoney(pensionRefundAmount, showCurrencyByCountry(payrollCountry));
    const confirmMessages: Record<string, { title: string; content: string }> =
      {
        hp: {
          title: `There is a Holiday Balance of ${formatMoney(hpBalance + erCostsBalance, showCurrencyByCountry(payrollCountry))}`,
          content: 'Do you want to pay all of the holiday balance?',
        },
        pension: {
          title: `There is a Pension Refund of ${formattedPensionRefund}`,
          content: `A pension refund of ${formattedPensionRefund} has been identified and must be paid out before leaving. If you choose to proceed, the refund will be processed and the pension membership will be ceased.`,
        },
        both: {
          title: `There is a Pension Refund of ${formattedPensionRefund} and a Holiday Balance of ${formatMoney(hpBalance + erCostsBalance, showCurrencyByCountry(payrollCountry))}`,
          content: `A pension refund of ${formattedPensionRefund} and a holiday balance of ${formatMoney(hpBalance + erCostsBalance, showCurrencyByCountry(payrollCountry))} has been identified and must be paid out before leaving. If you choose to proceed, the refund and the holiday balance will be processed and the pension membership will be ceased.`,
        },
      };

    const retainMessages: Record<string, string> = {
      hp: 'Are you sure you want to retain the Holiday Pay balance?',
      pension: 'Are you sure you want to retain the Pension Refund?',
      both: 'Are you sure you want to retain the Holiday Pay balance and Pension Refund?',
    };

    Modal.confirm({
      title: confirmMessages[type].title,
      content: confirmMessages[type].content,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        routerNavigate('/timesheets/add-new', {
          state: {
            selectSub: data,
            holidayPaid: hpBalance,
            erCosts: erCostsBalance,
            eePensionRefund,
            // Umbrella/Hybrid: pass ER refund; PAYE: zero (ER is employer's cost)
            erPensionRefund: isUmbrella ? erPensionRefund : 0,
            isIssueP45: true,
            isRefundPension: isPension,
            taxPeriod: lastestTaxPeriod,
          },
        });
      },
      onCancel: () => {
        Modal.confirm({
          title: retainMessages[type],
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => {
            setP45RetainDecision({
              retainedPensionRefund: isPension,
              retainedHolidayPay: isHp,
              eePensionRefund,
              erPensionRefund,
              holidayPayBalance: hpBalance,
              retainedErCosts: erCostsBalance,
            });
            setIsOpenSubmitP45Modal(true);
          },
        });
      },
    });
  };

  const handleIssueP45 = () => {
    if (!data?.id) return;

    const setting = data.setting;
    // NX-5493: read the backend round-once balance (big.js exact decimal) —
    // re-deriving in float64 here disagrees with the report/Balance column at
    // half-penny ties (e.g. accrued 762.3752 - paid 762.3702).
    const hpBalance = Number(setting?.holidayPayBalance || 0);
    // NX-5493: backend-computed in Setting.@AfterLoad — each ER component
    // rounded to its displayed 2dp figure, then summed. The raw
    // retainedHolidayPayErCosts sum can round to £0.01 while every column on
    // screen shows £0.00 (e.g. ERNI 0.0035 + App Levy 0.0016 = 0.0051),
    // which would prompt a payout no screen shows money for.
    // `??` (not `||`): this value gates AND seeds the P45 payout, so a
    // backend without the field must fail CLOSED to the old raw-sum figure
    // (worst case a cosmetic 1p prompt) — never silently read as £0.00 and
    // drop the ER on-costs from the payment.
    const erCostsBalance =
      setting?.retainedErCostsBalance ??
      Number((setting?.retainedHolidayPayErCosts || 0).toFixed(2));

    validateIssueP45(data.id).then((res) => {
      if (!res) return;

      if (res.isExistTsP45 || res.isProcessing) {
        showMessage({
          type: 'error',
          content: res.message ?? 'Validation failed',
        });
        return;
      }

      // Default the leaving date to the worker's last payment date
      if (res.lastestPaymentDate) {
        setP45DefaultDate(moment(res.lastestPaymentDate));
      }

      const isRefundPension = res.isRefundPension ?? false;
      const eePensionRefund = res.eePensionRefund ?? 0;
      const erPensionRefund = res.erPensionRefund ?? 0;

      // The reason only applies when the P45 proceeds WITHOUT a refund; a
      // stale value must not leak into the refund/retain path's modal.
      setP45NoRefundReason(
        !isRefundPension ? res.optOutState?.refundRejectionReason : undefined,
      );

      if (hpBalance > 0 || erCostsBalance > 0 || isRefundPension) {
        showConfirmP45(
          hpBalance,
          erCostsBalance,
          eePensionRefund,
          erPensionRefund,
          res.lastestTaxPeriod,
        );
        return;
      }

      setIsOpenSubmitP45Modal(true);
    });
  };

  // --- P60 Handlers ---
  const handleDownloadP60 = async (values: P60Values) => {
    if (!data?.id) return;
    const res = await downloadP60(data.id, { taxYear: values.taxYear });
    if (res?.data) {
      window.open(res.data);
    }
  };

  const handleSendEmailP60 = async (values: P60Values) => {
    if (!data?.id) return;
    const res = await sendEmailP60(data.id, { taxYear: values.taxYear });
    if (res?.success) {
      showMessage({
        type: 'success',
        content:
          messages['common.emailP60Success'] ?? 'P60 email sent successfully',
      });
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header
  // (always visible) instead of at the bottom of the form.
  const inDrawer = useDrawerSave(
    handleSave,
    isUpdatingEmployee || isUpdatingSettingEmployee,
    !readOnly,
  );

  if (!data)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  const initials =
    `${data.forename?.[0] ?? ''}${data.surname?.[0] ?? ''}`.toUpperCase();

  return (
    <div className="GeneralInformation">
      {/* P45 / Reinstatement action buttons */}
      {!isEmployee && isPayeeUmbrellaHybrid && (
        <div className="flex items-center justify-end gap-x-[16px] mb-4">
          {!isShowIssueP45 && (
            <AppButton
              variant="outlined"
              name="Reinstate Employee"
              onClick={() => setIsOpenReinstatementModal(true)}
            />
          )}
          {!isShowIssueP45 && !isIreland && (
            <>
              <AppButton
                variant="outlined"
                name={
                  messages['common.makePaymentAfterLeaving'] ??
                  'Make Payment After Leaving'
                }
                onClick={() =>
                  routerNavigate('/timesheets/add-new', {
                    state: { selectSub: data, isPAL: true },
                  })
                }
              />
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'download',
                      label: messages['common.download'] ?? 'Download',
                      onClick: handleDownloadP45,
                    },
                    {
                      key: 'email',
                      label: messages['common.email'] ?? 'Email',
                      onClick: handleEmailP45,
                    },
                  ],
                }}
              >
                <AppButton
                  variant="outlined"
                  name="P45"
                  rightIcon={<DownOutlined />}
                />
              </Dropdown>
            </>
          )}
          {isShowIssueP45 && (
            <AppButton
              variant="outlined"
              name={isIreland ? 'Mark Leaver' : 'Issue P45'}
              onClick={handleIssueP45}
              loading={isValidateP45Loading || isSubmitP45Loading}
            />
          )}
        </div>
      )}
      {isShowDownloadP60 && (
        <div className="flex items-center justify-end gap-x-[16px] mb-4">
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'download',
                  label: messages['common.download'] ?? 'Download',
                  onClick: () => setIsOpenP60DownloadModal(true),
                },
                ...(!isEmployee
                  ? [
                      {
                        key: 'email',
                        label: messages['common.email'] ?? 'Email',
                        onClick: () => setIsOpenP60EmailModal(true),
                      },
                    ]
                  : []),
              ],
            }}
          >
            <AppButton
              variant="outlined"
              name="P60"
              rightIcon={<DownOutlined />}
            />
          </Dropdown>
        </div>
      )}

      {/* Personal Detail Form */}
      <div>
        <Typography.Title
          level={5}
          className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
        >
          {messages['common.personalDetail']}
        </Typography.Title>

        {/* Avatar + Upload */}
        <div className="flex items-center gap-4 mb-8">
          {data.userRef?.[0]?.avatarUrl ? (
            <img
              src={data.userRef[0].avatarUrl}
              alt={initials}
              className="w-[88px] h-[88px] rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-[88px] h-[88px] rounded-full bg-[#D1FAE5] flex items-center justify-center flex-shrink-0">
              <span className="text-[24px] font-semibold text-[#065F46]">
                {initials}
              </span>
            </div>
          )}
        </div>

        <AppFormContainer
          form={form}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          disabled={readOnly}
        >
          {/* Row 1: Title | Forename */}
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.title']}
                name="title"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppSelect
                  options={titleOptions}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.forename']}
                name="forename"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseEnter'],
                  },
                ]}
              >
                <AppInput placeholder={messages['form.enterForename']} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Surname */}
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.surname']}
                name="surname"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseEnter'],
                  },
                ]}
              >
                <AppInput placeholder={messages['form.enterSurname']} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: Gender | Date of birth | Age */}
          <Row gutter={24}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={messages['common.gender']}
                name="gender"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppSelect
                  options={genderOptions}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={messages['common.dateOfBirth']}
                name="DOB"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppDatePicker
                  placeholder={messages['form.selectDate']}
                  format={'DD/MM/YYYY'}
                  maxDate={moment()}
                  minDate={moment().subtract(130, 'years')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={messages['common.age'] ?? 'Age'}>
                <AppInput
                  value={computedAge !== null ? String(computedAge) : ''}
                  placeholder="—"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          {/* NI Number / PPS Number */}
          <Form.Item
            label={getFieldLabelByCountry(payrollCountry, 'NINo')}
            name="NINumber"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value) return;
                  if (isIreland) {
                    if (!isValidPPSNumber(value)) {
                      throw new Error(
                        messages['form.invalidPPSNumber'] ??
                          'PPS Number format must be 7 digits followed by 1 or 2 letters',
                      );
                    }
                  } else if (!isValidNINumber(value)) {
                    throw new Error(
                      messages['form.invalidNINumber'] ?? 'Invalid NI number',
                    );
                  }
                },
              },
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              upperCaseAll
              maxLength={9}
            />
          </Form.Item>

          {/* Email */}
          <Form.Item
            label={messages['common.email']}
            name="email"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
              {
                type: 'email',
                message: messages['form.enterValidEmail'],
              },
            ]}
          >
            <AppInput
              prefix={<MailOutlined />}
              placeholder={messages['form.enter']}
              allowCapitalize={false}
              disabled={readOnlyContact}
            />
          </Form.Item>

          {/* Phone Number */}
          <Form.Item
            label={messages['common.mobile']}
            name="mobile"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!validatePhone(value)) {
                    return Promise.reject(messages['form.enterValidMobile']);
                  }
                  return Promise.resolve();
                },
              },
              {
                required:
                  data?.companyPayment?.autoEnrolSettings?.pensionProviderId ===
                  PensionProvidersName.SMART_PENSION,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              disabled={readOnlyContact}
            />
          </Form.Item>

          {/* Row: Marital Status | Nationality */}
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.maritalStatus']}
                name="maritalStatus"
              >
                <AppSelect
                  options={maritalStatusOption}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.nationality']}
                name="nationality"
              >
                <AppSelect
                  options={NationalList}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Right to Work Section */}
          <Typography.Title
            level={5}
            className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
          >
            {messages['common.rightToWork']}
          </Typography.Title>

          <Form.Item
            label={messages['common.rightToWorkStatus']}
            name="rightToWorkStatus"
          >
            <AppSelect
              options={defaultRightToWorkStatus}
              className="w-full !min-w-0"
              placeholder={messages['form.select']}
            />
          </Form.Item>

          <Form.Item
            label={messages['common.documentExpiryDate']}
            name="documentExpiryDate"
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
            />
          </Form.Item>

          <Form.Item label={messages['common.document']} name="document">
            <AppUploadFile
              urlFile={
                typeof watchValues?.document === 'string'
                  ? watchValues?.document
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldsValue({ document: file });
              }}
            />
          </Form.Item>

          <Form.Item
            label={
              messages['common.shareCodeECCheckCode'] ??
              'Share Code / EC Check Code'
            }
            name="shareCodeVerified"
            getValueFromEvent={(e) =>
              String(e?.target?.value ?? '').replace(/\s+/g, '')
            }
            rules={[
              {
                pattern: /^[A-Z0-9]{9}$/i,
                message:
                  (messages[
                    'prospect.onboarding.shareCode.invalid'
                  ] as string) ||
                  'Your share code must be exactly 9 characters long and can only contain letters (A-Z) and numbers (0-9).',
              },
            ]}
          >
            <AppInput
              placeholder={
                messages['prospect.onboarding.shareCode.placeholder'] as string
              }
              maxLength={15}
            />
          </Form.Item>

          {/* Restricted Hours — NX-5228: the 20-hour check only runs for UK
              payrolls (getIsRestrictedHoursValidation, Tier 4 student visa). */}
          {!isIreland && (
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-medium">
                {messages['common.restrictedHours']}?
              </span>
              <Tooltip
                title={
                  messages['common.restrictedHoursTooltip'] ??
                  'If enabled it will flag if employee works over restricted hours'
                }
              >
                <InfoCircleOutlined className="cursor-pointer" />
              </Tooltip>
              <Form.Item
                name="isRestrictedHours"
                valuePropName="checked"
                className="!mb-0"
              >
                <AppSwitch />
              </Form.Item>
            </div>
          )}

          {/* ========== Registration Detail (non-employee only) ========== */}
          {!isEmployee && (
            <>
              <Divider />
              <Typography.Title
                level={5}
                className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
              >
                {messages['common.registrationDetail']}
              </Typography.Title>

              {isBureau ? (
                <>
                  {/* UK Layout */}
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.employer']}
                        name="paymentCompany"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelectSync
                          type="company"
                          apiType="searchCompany"
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('paymentCompany', value);
                            form.setFieldValue('department', null);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.status']}
                        name="status"
                      >
                        <AppSelect
                          options={defaultStatusOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          allowClear={false}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.dateOfJoining']}
                        name="dateOfJoining"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppDatePicker
                          placeholder={messages['form.selectDate']}
                          format={'DD/MM/YYYY'}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.systemId']}
                        name="systemId"
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.employerRef'] || 'Employer Ref'}
                        name="agencyRef"
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.registrationStatus']}
                        name="registrationStatus"
                      >
                        <AppSelect
                          options={regStatusEditOptions as any}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.payFrequency']}
                        name="payFrequency"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultPaymentFrequency}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.jobTitle']}
                        name="jobDescription"
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.department']}
                        name="department"
                      >
                        <AppSelect
                          options={(
                            watchValues?.paymentCompany?.fullData
                              ?.departmentList || []
                          ).map((name: string) => ({
                            label: name,
                            value: name,
                          }))}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    {!isIreland && (
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.workingTimeRegulation']}
                        name="workingTimeRegulation"
                      >
                        <AppSelect
                          options={regulationOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    )}
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.dateLeft']}
                        name="dateLeft"
                      >
                        <AppDatePicker
                          placeholder={messages['form.selectDate']}
                          format={'DD/MM/YYYY'}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.reason']}
                        name="reason"
                      >
                        <AppSelect
                          options={reasonOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {/* Non-UK Layout (original) */}
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={
                          isBureau
                            ? messages['common.employer']
                            : messages['common.paymentCompany']
                        }
                        name="paymentCompany"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelectSync
                          type="company"
                          apiType="searchCompany"
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          onChangeValue={(value: any) => {
                            form.setFieldValue('paymentCompany', value);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.status']}
                        name="status"
                      >
                        <AppSelect
                          options={defaultStatusOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          allowClear={false}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.dateOfJoining']}
                        name="dateOfJoining"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppDatePicker
                          placeholder={messages['form.selectDate']}
                          format={'DD/MM/YYYY'}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.dateLeft']}
                        name="dateLeft"
                      >
                        <AppDatePicker
                          placeholder={messages['form.selectDate']}
                          format={'DD/MM/YYYY'}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.payMethod']}
                        name="payMethod"
                      >
                        <AppSelect
                          options={getPaymentMethodOptions(isIreland)}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={`${messages['common.rate']}/${messages['common.hour']}`}
                        name="rateHour"
                      >
                        <AppCurrencyInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {isBureau && (
                    <Form.Item
                      label={messages['common.annualSalary']}
                      name="annualSalary"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  )}

                  <Row gutter={24}>
                    {!isIreland && (
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.workingTimeRegulation']}
                        name="workingTimeRegulation"
                      >
                        <AppSelect
                          options={regulationOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    )}
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={
                          isBureau || isBureauEmployer
                            ? messages['common.employerRef'] || 'Employer Ref'
                            : messages['common.agencyRef']
                        }
                        name="agencyRef"
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.registrationStatus']}
                        name="registrationStatus"
                      >
                        <AppSelect
                          options={regStatusEditOptions as any}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.payFrequency']}
                        name="payFrequency"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultPaymentFrequency}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.reason']}
                        name="reason"
                      >
                        <AppSelect
                          options={reasonOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.jobTitle']}
                        name="jobDescription"
                      >
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item label={messages['common.hours']} name="hours">
                        <AppRateInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                    {!isIreland && (
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.agencyConductRegulation']}
                        name="agencyConductRegulation"
                      >
                        <AppSelect
                          options={regulationOptions}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    )}
                  </Row>
                </>
              )}

              {/* ========== Emergency Contact ========== */}
              <Divider />
              <Typography.Title
                level={5}
                className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
              >
                {messages['common.emergencyContact']}
              </Typography.Title>

              <Form.Item label={messages['common.name']} name="emergencyName">
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>

              <Form.Item
                label={messages['common.address1']}
                name="emergencyAddress1"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['common.address2']}
                name="emergencyAddress2"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
              <Form.Item
                label={messages['common.address3']}
                name="emergencyAddress3"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.cityTown']}
                    name="emergencyCity"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.county']}
                    name="emergencyCounty"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      isIreland
                        ? messages['common.eirCode']
                        : messages['common.postCode']
                    }
                    name="emergencyPostCode"
                    rules={[postCodeValidationRule(messages, payrollCountry)]}
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      value={watchValues?.emergencyPostCode}
                      upperCaseAll
                      onChange={(e) => {
                        form.setFieldValue(
                          'emergencyPostCode',
                          formatPostCode(e.target.value, payrollCountry),
                        );
                      }}
                      maxLength={8}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.country']}
                    name="emergencyCountry"
                  >
                    <AppSelect
                      options={countryList}
                      placeholder={
                        messages['assignment.address.selectCountry'] ??
                        'Country'
                      }
                      className="!min-w-0 flex-1"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.mobile']}
                    name="emergencyMobile"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (!validatePhone(value)) {
                            return Promise.reject(
                              messages['form.enterValidMobile'],
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.email']}
                    name="emergencyEmail"
                    rules={[
                      {
                        type: 'email',
                        message: messages['form.enterValidEmail'],
                      },
                    ]}
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      allowCapitalize={false}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ========== System Access ========== */}
              <Divider />
              <div className="flex items-center justify-between !mb-6">
                <Typography.Title
                  level={5}
                  className="!mb-0 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
                >
                  {messages['common.systemAccess']}
                </Typography.Title>
                {data?.allowPortalLogin && onResendLogin && (
                  <AppButton
                    name={messages['common.resendLoginId']}
                    variant="outlined"
                    loading={isResendLoginLoading}
                    onClick={onResendLogin}
                  />
                )}
              </div>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.allowPortalLogin']}
                    name="allowPortalLogin"
                    valuePropName="value"
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label={messages['common.loginId']} name="loginId">
                    <AppInput
                      placeholder={messages['form.enter']}
                      allowCapitalize={false}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* ========== Employee Registration Fields (read-only) ========== */}
          {isEmployee && (
            <>
              <Divider />
              <Typography.Title
                level={5}
                className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
              >
                {messages['common.registrationDetail']}
              </Typography.Title>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.dateOfJoining']}
                    name="dateOfJoining"
                  >
                    <AppDatePicker
                      placeholder={messages['form.selectDate']}
                      format={'DD/MM/YYYY'}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      isBureau
                        ? messages['common.employer']
                        : messages['common.paymentCompany']
                    }
                    name="paymentCompany"
                  >
                    <AppSelectSync
                      type="company"
                      apiType="searchCompany"
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.payMethod']}
                    name="payMethod"
                  >
                    <AppSelect
                      options={getPaymentMethodOptions(isIreland)}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.jobTitle']}
                    name="jobDescription"
                  >
                    <AppInput placeholder={messages['form.enter']} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={`${messages['common.rate']}/${messages['common.hour']}`}
                    name="rateHour"
                  >
                    <AppCurrencyInput placeholder={messages['form.enter']} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.annualSalary']}
                    name="annualSalary"
                  >
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      placeholder={messages['form.enter']}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* Action Buttons */}
          {!readOnly && !inDrawer && (
            <div className="flex items-center justify-start gap-3 pt-4">
              <AppButton
                type="primary"
                name={messages['common.save']}
                leftIcon={
                  <Icons.IconCheckCircle
                    color={defaultTheme.theme.palette.white}
                  />
                }
                onClick={handleSave}
                loading={isUpdatingEmployee || isUpdatingSettingEmployee}
              />
            </div>
          )}
        </AppFormContainer>
      </div>

      {/* Modals */}
      <SubmitIssueP45Modal
        isOpen={isOpenSubmitP45Modal}
        toggle={() => setIsOpenSubmitP45Modal(false)}
        onSubmit={handleSubmitIssueP45}
        title={
          isIreland
            ? (messages['common.markLeaver'] ?? 'Mark Leaver')
            : (messages['common.issueP45'] ?? 'Issue P45')
        }
        defaultDate={p45DefaultDate}
        notice={p45NoRefundReason}
      />
      {data && (
        <ReinstatementModal
          isOpen={isOpenReinstatementModal}
          toggle={() => setIsOpenReinstatementModal(false)}
          data={data}
          onSuccess={() => onRefetch?.()}
        />
      )}
      <ModalReP45
        isOpen={isOpenReP45Modal}
        toggle={() => setIsOpenReP45Modal(false)}
        onSubmit={handleReP45Submit}
        dataIssuedP45={(data as any)?.issuedP45s ?? []}
        action={reP45Action}
      />
      <P60Modal
        title={messages['common.downloadP60'] ?? 'Download P60'}
        isOpen={isOpenP60DownloadModal}
        toggle={() => setIsOpenP60DownloadModal(false)}
        onSubmit={handleDownloadP60}
        subcontractorId={data?.id}
        isIssuedP45={!!data?.isIssuedP45}
        p60TaxYears={data?.p60TaxYears}
      />
      <P60Modal
        title={messages['common.emailP60'] ?? 'Email P60'}
        isOpen={isOpenP60EmailModal}
        toggle={() => setIsOpenP60EmailModal(false)}
        onSubmit={handleSendEmailP60}
        subcontractorId={data?.id}
        isIssuedP45={!!data?.isIssuedP45}
        p60TaxYears={data?.p60TaxYears}
      />
    </div>
  );
};

export default GeneralInformation;
